import { useState, type ReactNode } from 'react';

import type { Pricing } from '../../lib/types';
import { AirExtrasIcons, BagIcon, Caret, Chevron } from './SellDisplay';

/** The three markers the host draws under the total: settings, select, save. */
function TotalMarkers() {
  return (
    <span className="priced__markers" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="15" height="15">
        <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M12 2.8v2.6M12 18.6v2.6M2.8 12h2.6M18.6 12h2.6M5.5 5.5l1.9 1.9M16.6 16.6l1.9 1.9M5.5 18.5l1.9-1.9M16.6 7.4l1.9-1.9"
          stroke="currentColor"
          strokeWidth="1.6"
        />
      </svg>
      <svg viewBox="0 0 24 24" width="15" height="15">
        <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
      <svg viewBox="0 0 24 24" width="15" height="15">
        <path d="M6.5 3.5h11v17L12 16l-5.5 4.5z" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    </span>
  );
}

function ExternalLink() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M14 4h6v6M20 4l-8.5 8.5M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="sold__tile priced__tile">
      <span className="sold__tile-label">{label}</span>
      {children}
    </div>
  );
}

/**
 * A priced itinerary: the purchase deadline, the fare summary block on the
 * slate (which opens onto the fare detail panel), and the fare calculation
 * printed under it.
 */
interface PricingDisplayProps {
  pricing: Pricing;
  /** Stores this price as a price quote record (WPRQ). */
  onSavePrice: () => void;
}

export function PricingDisplay({ pricing: p, onSavePrice }: PricingDisplayProps) {
  const [open, setOpen] = useState(false);
  const [basis, setBasis] = useState(p.fareBasis[0] ?? '');
  const [heading, ...rows] = p.summary;

  return (
    <div className="priced">
      <div className="priced__header">{p.header}</div>

      <button
        type="button"
        className={`priced__block${open ? ' priced__block--open' : ''}`}
        aria-expanded={open}
        aria-label={`${open ? 'Hide' : 'Show'} fare details, total ${p.total}`}
        onClick={() => setOpen((o) => !o)}
      >
        <pre className="priced__summary">
          <b>{heading}</b>
          {'\n'}
          {rows.join('\n')}
        </pre>
        <span className="priced__total">
          <span>TOTAL:&nbsp; {p.total}</span>
          <TotalMarkers />
        </span>
        <span aria-hidden="true" />
        <span className="sold__expand">
          <Chevron open={open} />
        </span>
      </button>

      {open && (
        <div className="sold__panel priced__panel">
          <div className="priced__info">
            <span className="priced__info-icon" aria-hidden="true">
              i
            </span>
            No Alternate Fares Found
          </div>

          <div className="priced__pax">
            <span className="sold__badge">{p.passenger}</span>
          </div>

          <div className="priced__rules">
            <select className="priced__basis" value={basis} onChange={(e) => setBasis(e.target.value)}>
              {p.fareBasis.map((b, i) => (
                <option key={`${b}-${i}`} value={b}>
                  {b}
                </option>
              ))}
            </select>
            <div className="priced__rules-text">
              <div className="priced__rules-line">
                {p.rules.map((r) => (
                  <span key={r.label}>
                    {r.label} : {r.value}
                  </span>
                ))}
              </div>
              <div>LAST DAY TO BOOK : {p.lastDayToBook}</div>
            </div>
            <span className="priced__link" title="Not implemented">
              <ExternalLink />
            </span>
          </div>

          <div className="priced__notes">
            <span className="priced__notes-label">Notes</span>
            <div>
              {p.notes.map((n) => (
                <div key={n}>{n}</div>
              ))}
            </div>
          </div>

          {/* Drawn as the host draws them; the services behind them are not
              built yet. */}
          <div className="sold__tiles">
            <Tile label="Taxes/Fees/Charges & Fare Calc">
              <span className="sold__tile-value">View All</span>
            </Tile>
            <Tile label="Baggage Disclosures & Embargo Information">
              <span className="sold__tile-value">View All</span>
            </Tile>
            <Tile label="Seat Map">
              <span className="sold__tile-value">Seat Map</span>
            </Tile>
            <Tile label="Air Extras">
              <AirExtrasIcons />
            </Tile>
            <Tile label="Branded Fares">
              <span className="sold__tile-value">No Branded Fares</span>
            </Tile>
            <Tile label="Est Emissions">
              <span className="sold__tile-value priced__emissions">{p.emissions}</span>
            </Tile>
          </div>

          <div className="sold__actions">
            <span className="sold__actions-spacer" />
            <button type="button" className="sold__button" title="Not implemented" disabled>
              Modify <Caret />
            </button>
            <button type="button" className="sold__button" title="Not implemented" disabled>
              Re-Pricing Options <Caret />
            </button>
            <button type="button" className="sold__button sold__button--primary" onClick={onSavePrice}>
              Save Price
            </button>
          </div>
        </div>
      )}

      <pre className="priced__lines">
        <span className="priced__pax-line">
          {p.paxCode}
          <span className="priced__bag">
            <BagIcon size={16} />
          </span>
          {p.fareBasis.join('/')}
        </span>
        {'\n'}
        {p.lines.join('\n')}
      </pre>
    </div>
  );
}
